import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

import type {
    IDeleteIntegrationSecretParams,
    IOrganizationIntegration,
    ISaveIntegrationSecretParams,
    IValidateIntegrationParams,
} from "../../interfaces";

const LIST_QUERY_KEY = "integrations";

export default function useIntegration() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const queryClient = useQueryClient();

    function useList(organizationId: string | undefined) {
        return useQuery({
            queryKey: [LIST_QUERY_KEY, organizationId ?? "none"],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/integration/list", {
                    params: { OrganizationId: organizationId },
                });
                return parseSuccessResponseData(response) as IOrganizationIntegration[];
            },
            enabled: !!organizationId,
        });
    }

    function useCreateSecret() {
        return useMutation({
            mutationFn: (parameters: ISaveIntegrationSecretParams) =>
                axiosPrivate.post("/v1/integration/create-secret", parameters).then(parseSuccessResponseData),
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: [LIST_QUERY_KEY, variables.OrganizationId],
                });
            },
        });
    }

    function useUpdateSecret() {
        return useMutation({
            mutationFn: (parameters: ISaveIntegrationSecretParams) =>
                axiosPrivate.post("/v1/integration/update-secret", parameters).then(parseSuccessResponseData),
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: [LIST_QUERY_KEY, variables.OrganizationId],
                });
            },
        });
    }

    function useDeleteSecret() {
        return useMutation({
            mutationFn: (parameters: IDeleteIntegrationSecretParams) =>
                axiosPrivate
                    .delete("/v1/integration/delete-secret", {
                        params: parameters,
                    })
                    .then(parseSuccessResponseData),
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: [LIST_QUERY_KEY, variables.OrganizationId],
                });
            },
        });
    }

    function useValidate() {
        return useMutation({
            mutationFn: (parameters: IValidateIntegrationParams) =>
                axiosPrivate.post("/v1/external-integration/validate", parameters).then(parseSuccessResponseData),
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: [LIST_QUERY_KEY, variables.OrganizationId],
                });
            },
        });
    }

    return {
        useList,
        useCreateSecret,
        useUpdateSecret,
        useDeleteSecret,
        useValidate,
    };
}
